// src/redux/slices/testimonialsSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { testimonialsService } from '../../services/testimonialsService.js'
import { handleApiError } from '../../utils/handleApiError.js'

export const fetchTestimonials = createAsyncThunk(
    'testimonials/fetchTestimonials',
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await testimonialsService.getTestimonials(params)
            return response
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const fetchFeaturedTestimonials = createAsyncThunk(
    'testimonials/fetchFeaturedTestimonials',
    async (limit = 6, { rejectWithValue }) => {
        try {
            const response = await testimonialsService.getTestimonials({
                featured: true,
                limit
            })
            return response
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const createTestimonial = createAsyncThunk(
    'testimonials/createTestimonial',
    async (testimonialData, { rejectWithValue }) => {
        try {
            const response = await testimonialsService.createTestimonial(testimonialData)
            return response
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const updateTestimonial = createAsyncThunk(
    'testimonials/updateTestimonial',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const response = await testimonialsService.updateTestimonial(id, data)
            return response
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const deleteTestimonial = createAsyncThunk(
    'testimonials/deleteTestimonial',
    async (id, { rejectWithValue }) => {
        try {
            await testimonialsService.deleteTestimonial(id)
            return id
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

const testimonialsSlice = createSlice({
    name: 'testimonials',
    initialState: {
        testimonials: [],
        featuredTestimonials: [],
        currentTestimonial: null,
        loading: false,
        creating: false,
        updating: false,
        deleting: false,
        error: null,
        hasMore: true
    },
    reducers: {
        clearCurrentTestimonial: (state) => {
            state.currentTestimonial = null
        },
        clearError: (state) => {
            state.error = null
        },
        clearTestimonials: (state) => {
            state.testimonials = []
            state.featuredTestimonials = []
            state.hasMore = true
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch Testimonials
            .addCase(fetchTestimonials.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(fetchTestimonials.fulfilled, (state, action) => {
                state.loading = false
                const newTestimonials = action.payload.data || action.payload

                if (action.payload.page > 1) {
                    state.testimonials = [...state.testimonials, ...newTestimonials]
                } else {
                    state.testimonials = newTestimonials
                }

                state.hasMore = newTestimonials.length === (action.payload.pagination?.limit || 10)
                state.error = null
            })
            .addCase(fetchTestimonials.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })
            // Fetch Featured Testimonials
            .addCase(fetchFeaturedTestimonials.pending, (state) => {
                state.loading = true
            })
            .addCase(fetchFeaturedTestimonials.fulfilled, (state, action) => {
                state.loading = false
                state.featuredTestimonials = action.payload.data || action.payload
                state.error = null
            })
            .addCase(fetchFeaturedTestimonials.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })
            // Create Testimonial
            .addCase(createTestimonial.pending, (state) => {
                state.creating = true
                state.error = null
            })
            .addCase(createTestimonial.fulfilled, (state, action) => {
                state.creating = false
                state.testimonials.unshift(action.payload)
                state.error = null
            })
            .addCase(createTestimonial.rejected, (state, action) => {
                state.creating = false
                state.error = action.payload
            })
            // Update Testimonial
            .addCase(updateTestimonial.pending, (state) => {
                state.updating = true
                state.error = null
            })
            .addCase(updateTestimonial.fulfilled, (state, action) => {
                state.updating = false
                const updatedTestimonial = action.payload
                const index = state.testimonials.findIndex(t => t._id === updatedTestimonial._id)
                if (index !== -1) {
                    state.testimonials[index] = updatedTestimonial
                }

                // Update featured testimonials if needed
                const featuredIndex = state.featuredTestimonials.findIndex(t => t._id === updatedTestimonial._id)
                if (featuredIndex !== -1) {
                    state.featuredTestimonials[featuredIndex] = updatedTestimonial
                }

                state.error = null
            })
            .addCase(updateTestimonial.rejected, (state, action) => {
                state.updating = false
                state.error = action.payload
            })
            // Delete Testimonial
            .addCase(deleteTestimonial.pending, (state) => {
                state.deleting = true
                state.error = null
            })
            .addCase(deleteTestimonial.fulfilled, (state, action) => {
                state.deleting = false
                const deletedId = action.payload
                state.testimonials = state.testimonials.filter(t => t._id !== deletedId)
                state.featuredTestimonials = state.featuredTestimonials.filter(t => t._id !== deletedId)
                state.error = null
            })
            .addCase(deleteTestimonial.rejected, (state, action) => {
                state.deleting = false
                state.error = action.payload
            })
    }
})

export const {
    clearCurrentTestimonial,
    clearError,
    clearTestimonials
} = testimonialsSlice.actions

export default testimonialsSlice.reducer